<template>
  <div>
    <MissedCallsBanner
      v-if="showMarketContext"
      :total-missed-calls="totalMissedCalls"
      :campaigns-below-market="campaignsBelowMarketCount"
      :window-label="marketWindow.long"
      :auctions-wording="showMarketIntelligence"
      class="m-4 mb-0"
      @review="sortByMissedCalls"
    />

    <!--
      As on Call Logs: a header-less panel of cards above the list. Today over all of the account's
      campaigns, from the same snapshot as the column, plus how many of the listed campaigns are active.
    -->
    <Panel
      v-if="showLiveRpc"
      hide-header
      class="campaign-rpc-stats"
      data-cy="campaign-rpc-cards"
    >
      <template #content>
        <div class="d-flex flex-wrap campaign-rpc-stats__cards">
          <ReportingCardStats
            :icon="['fas', 'coins']"
            stats-title="RPC today"
            :stats-text="campaignRpcCards.rpc"
            :secondary-text="campaignRpcCards.rpcNote"
            :icon-color="CAMPAIGN_RPC_ICON_COLORS.rpc"
            data-cy="card-rpc-today"
          />
          <ReportingCardStats
            :icon="['fas', 'phone-volume']"
            stats-title="Calls today"
            :stats-text="campaignRpcCards.calls"
            :icon-color="CAMPAIGN_RPC_ICON_COLORS.calls"
            data-cy="card-calls-today"
          />
          <ReportingCardStats
            :icon="['fas', 'money-check-alt']"
            stats-title="Total today"
            :stats-text="campaignRpcCards.revenue"
            :icon-color="CAMPAIGN_RPC_ICON_COLORS.revenue"
            data-cy="card-total-today"
          />
          <!-- The Campaigns panel's own icon, in green -->
          <ReportingCardStats
            :icon="['fas', 'chart-line']"
            stats-title="Active campaigns"
            :stats-number="activeCampaignsCount"
            :icon-color="CAMPAIGN_RPC_ICON_COLORS.active"
            data-cy="card-active-campaigns"
          />
        </div>
      </template>
    </Panel>

    <Panel
      :icon="['fas', 'chart-line']"
      :class="{ 'campaigns-panel--below-stats': showLiveRpc }"
      loader-text="Loading Campaigns"
      :loading="loading"
      :show-loading-error="showLoadingError"
      :loading-error-text="loadingErrorText"
    >
      <template #header>
        <span data-cy="heading">Campaigns</span>
      </template>
      <template #content>
        <!--
          Staff-only note. The two things impersonation deliberately does NOT carry are creating a
          softphone campaign (its destination is composed from the acting user, so an admin's would
          ring an agent id with no credential) and going live (that registers a SIP identity, which
          must stay the agent's own). Editing is fine. Says so up front rather than leaving an
          admin hunting for a Create item and a Go Live button that are not coming.
        -->
        <b-alert
          v-if="showSoftphoneAdminNotice"
          show
          variant="info"
          class="p-2"
          data-cy="softphone-admin-notice"
        >
          You are viewing this account as an admin. You can edit its softphone campaigns, but only
          the account's own softphone agents can create one or take it live.
        </b-alert>

        <!-- The market endpoint stopped at its row cap. The campaigns past it would otherwise
             render a dash exactly like a market with no auctions, and drop out of the banner's
             missed-calls total, with nothing on screen to say either is incomplete -->
        <b-alert
          v-if="showMarketContext && campaignMarketPositionsTruncated"
          show
          variant="warning"
          class="p-2"
          data-cy="market-positions-truncated"
        >
          {{ truncatedMarketPositionsText }}
        </b-alert>

        <!--
          A card per campaign on a phone, so that Go Live is on the screen rather than at the far end
          of a table scrolled sideways, and its actions pinned on a touch screen (see TouchTable).
        -->
        <TouchTable
          data-cy="table-buyer-campaigns"
          :tbody-tr-attr="{ 'data-cy': 'table-row' }"
          :items="campaignsWithMarketData"
          :fields="visibleFields"
          primary-key="id"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-compare="compareLiveRpcForTable"
        >
          <!--
            A softphone campaign's row says what the WIDGET says - see `statusText`. The agent reads
            this table and the widget together, and one of them calling the same moment "Active" and
            the other "Ready" leaves them working out which is which.
          -->
          <template #cell(status)="row">
            <b-badge
              class="campaign-status"
              :variant="statusVariant(row.item)"
              data-cy="campaign-status"
            >
              {{ statusText(row.item) }}
            </b-badge>
          </template>

          <template #cell(category.name)="row">
            {{ row.item.category.name }}
            <b-badge
              v-if="row.item.category.isCpaConversionOnly"
              variant="info"
              class="ml-1"
              title="CPA Conversion Only Category"
            >
              CPA
            </b-badge>
          </template>

          <template #cell(maxBidPerCallInUSD)="row">
            <!-- Static campaigns get the same inline bid input the dashboard uses; RTB and
                 Programmatic bids are set by the bidder, so they stay read-only: inside the
                 rx-market-intelligence rollout, what the campaign bids now where there is a number
                 (R65), and "Dynamic" otherwise -->
            <!-- Keyed on the campaign alone: the input follows a changed bid itself, so a save
                 or a raise never remounts it under the buyer's cursor, which would also flush
                 whatever they were in the middle of typing -->
            <CampaignBidInput
              v-if="isStaticCampaign(row.item)"
              :key="row.item.id"
              class="inline-bid-input"
              :campaign-id="row.item.id"
              :initial-value="Number(row.item.budget.maxBidPerCallInUSD)"
              :min-bid="getCategoryMinBid(row.item)"
              @saved="(value, previous) => onInlineBidSaved(row.item, value, previous)"
            />
            <DynamicBidTerms
              v-else-if="getBidColumnTermsFor(row.item)"
              :terms="getBidColumnTermsFor(row.item)"
              :is-cpa-only="isCpaOnlyCampaign(row.item)"
              :window-long="marketWindow.long"
            />
            <template v-else>
              {{ getMaxBidPerCall(row.item) }}
            </template>
          </template>

          <template #head(liveRpc)="data">
            {{ data.label }} <span class="campaign-rpc-head-today">(today)</span>
          </template>

          <template #head(liveCalls)="data">
            {{ data.label }} <span class="campaign-rpc-head-today">(today)</span>
          </template>

          <template #head(liveRevenue)="data">
            {{ data.label }} <span class="campaign-rpc-head-today">(today)</span>
          </template>

          <!-- No figure, or zero, reads as the market columns' muted "-", so every empty cell in a row looks the same -->
          <template #cell(liveRpc)="row">
            <span
              data-cy="live-rpc"
              :class="{ 'text-muted': !row.item.liveRpc }"
            >{{ !row.item.liveRpc ? '-' : formatLiveRpc(row.item.liveRpc) }}</span>
          </template>

          <template #cell(liveCalls)="row">
            <span
              data-cy="live-calls"
              :class="{ 'text-muted': !row.item.liveCalls }"
            >{{ !row.item.liveCalls ? '-' : formatLiveCalls(row.item.liveCalls) }}</span>
          </template>

          <template #cell(liveRevenue)="row">
            <span
              data-cy="live-revenue"
              :class="{ 'text-muted': !row.item.liveRevenue }"
            >{{ !row.item.liveRevenue ? '-' : formatLiveRpc(row.item.liveRevenue) }}</span>
          </template>

          <!-- The three market headers explain themselves on hover over the label, no "?" icon -->
          <template #head(marketPosition)="data">
            <span
              v-b-tooltip.hover.left
              :title="marketPositionTooltip"
              class="campaign-head-hint"
              data-cy="tooltip-market-position"
            >{{ data.label }}</span>
          </template>

          <template #cell(marketPosition)="row">
            <!-- The bid marker comes from the endpoint's positionedBidInUSD, never from the
                 campaign config, so the marker always sits on the exact bid the tier was
                 computed from - configured max bid for Static, the median observed over the
                 market window for RTB/Programmatic - and the two can never drift -->
            <MarketPositionIndicator
              :market-position="row.item.marketPosition"
              :bid-in-usd="getPositionedBid(row.item)"
              :window-short="marketWindow.short"
              :is-static="isStaticCampaign(row.item)"
              :market-intelligence="showMarketIntelligence"
              :routing-type="getRoutingType(row.item)"
              :is-cpa-only="isCpaOnlyCampaign(row.item)"
            />
          </template>

          <!-- The window muted under the label, as RPC (today) has it; the field's own label, which
               the stacked layout on a phone shows, keeps the short "Missed (7d)" -->
          <template #head(missedCalls)>
            <span
              v-b-tooltip.hover.left
              :title="missedCallsTooltip"
              class="campaign-head-hint"
              data-cy="tooltip-missed-calls"
            >Missed <span
              v-if="missedCallsWindowText"
              class="campaign-rpc-head-today"
            >({{ missedCallsWindowText }})</span></span>
          </template>

          <template #cell(missedCalls)="row">
            <span
              v-if="row.item.missedCalls !== null"
              :class="getMissedCallsClass(row.item)"
            >
              {{ row.item.missedCalls.toLocaleString() }}
            </span>
            <span
              v-else
              class="text-muted"
            >-</span>
          </template>

          <template #head(opportunity)="data">
            <span
              v-b-tooltip.hover.left
              :title="opportunityTooltip"
              class="campaign-head-hint"
              data-cy="tooltip-opportunity"
            >{{ data.label }}</span>
          </template>

          <template #cell(opportunity)="row">
            <BidOpportunityCell
              :campaign="row.item"
              :market-position="row.item.marketPosition"
              :is-static="isStaticCampaign(row.item)"
              :window-short="marketWindow.short"
              :window-long="marketWindow.long"
              :market-intelligence="showMarketIntelligence"
              :routing-type="getRoutingType(row.item)"
              :programmatic-bid-settings="getProgrammaticBidSettings(row.item)"
              :is-cpa-only="isCpaOnlyCampaign(row.item)"
              @apply="showConfirmRaiseBid"
            />
          </template>

          <template #cell(dailyBudgetInUSD)="row">
            {{ formatCurrency(row.item.budget.dailyBudgetInUSD) }}
          </template>

          <template #cell(actions)="row">
            <b-button
              v-if="canPauseCampaign(row.item) && !isCampaignPaused(row.item)"
              v-b-tooltip.hover.left
              class="btn-icon bg-transparent border-0 p-0"
              size="sm"
              title="Pause Campaign"
              @click="showConfirmPauseBuyerCampaign(row.item)"
            >
              <font-awesome-icon
                :icon="['fa', 'pause']"
              />
            </b-button>
            <b-button
              v-if="canPauseCampaign(row.item) && isCampaignPaused(row.item)"
              v-b-tooltip.hover.left
              class="btn-icon bg-transparent border-0 p-0"
              size="sm"
              title="Resume Campaign"
              @click="showConfirmResumeBuyerCampaign(row.item)"
            >
              <font-awesome-icon
                :icon="['fa', 'play']"
              />
            </b-button>
            <b-button
              v-if="showSoftphoneButton(row.item)"
              v-b-tooltip.hover.left
              class="campaign-go-live"
              size="sm"
              :variant="isLiveSoftphoneCampaign(row.item) ? 'danger' : 'success'"
              :disabled="isSoftphoneButtonDisabled(row.item)"
              :title="softphoneButtonTitle(row.item)"
              :data-cy="isLiveSoftphoneCampaign(row.item) ? 'button-softphone-go-offline' : 'button-softphone-go-live'"
              @click="toggleSoftphoneForCampaign(row.item)"
            >
              <font-awesome-icon
                :icon="['fa', isLiveSoftphoneCampaign(row.item) ? 'phone-slash' : 'phone']"
                class="mr-1"
              />
              {{ isLiveSoftphoneCampaign(row.item) ? 'Go Offline' : 'Go Live' }}
            </b-button>
            <b-button
              :to="{
                name: editRouteName(row.item),
                params: { id: row.item.id }
              }"
              data-cy="button-edit"
              class="campaign-edit btn-icon bg-transparent border-0 p-0"
              size="sm"
            >
              <font-awesome-icon
                :icon="['fa', 'edit']"
              />
            </b-button>
            <!--
              A disabled button says why only in its tooltip, and a finger can neither hover nor tap a
              disabled button. On a touch screen the reason is written under it instead.
            -->
            <small
              v-if="showSoftphoneButton(row.item) && isSoftphoneButtonDisabled(row.item)"
              class="campaign-go-live-reason text-muted"
              data-cy="softphone-go-live-reason"
            >
              {{ softphoneButtonTitle(row.item) }}
            </small>
          </template>
        </TouchTable>
      </template>
    </Panel>

    <b-modal
      id="confirm-pause-modal"
      ref="confirm-pause-modal"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Confirm Pause"
      ok-title="Pause"
      button-size="sm"
      @ok="pauseBuyerCampaign"
    >
      <p
        v-if="selectedBuyerCampaign"
        class="mb-2"
      >
        Are you sure you want to pause <strong>{{ selectedBuyerCampaign.name }}</strong>?
      </p>

      <template #modal-footer>
        <b-button
          type="button"
          variant="secondary"
          size="sm"
          :disabled="isLoading"
          @click="$bvModal.hide('confirm-pause-modal')"
        >
          Cancel
        </b-button>

        <BaseButtonLoader
          :loading="isLoading"
          class="ml-2"
          type="button"
          :custom-button="false"
          size="sm"
          variant="primary"
          @click="pauseBuyerCampaign"
        >
          Pause
        </BaseButtonLoader>
      </template>
    </b-modal>

    <b-modal
      id="confirm-resume-modal"
      ref="confirm-resume-modal"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Confirm Resume"
      ok-title="Resume"
      button-size="sm"
      @ok="resumeBuyerCampaign"
    >
      <p
        v-if="selectedBuyerCampaign"
        class="mb-2"
      >
        Are you sure you want to resume <strong>{{ selectedBuyerCampaign.name }}</strong>?
      </p>

      <template #modal-footer>
        <b-button
          type="button"
          variant="secondary"
          size="sm"
          :disabled="isLoading"
          @click="$bvModal.hide('confirm-resume-modal')"
        >
          Cancel
        </b-button>

        <BaseButtonLoader
          :loading="isLoading"
          class="ml-2"
          type="button"
          :custom-button="false"
          size="sm"
          variant="primary"
          @click="resumeBuyerCampaign"
        >
          Resume
        </BaseButtonLoader>
      </template>
    </b-modal>

    <!-- The confirmation for a suggestion chip. Today's raise wording, and with Slice H's chips
         (rx-market-intelligence) the switch of bid and minimum, the minimum-only change (I19),
         the direction of cost and the minimum's reason when the minimum changes (I14, I18), and
         the row's Missed count, the one count a buyer sees, left out at 0 (R54) -->
    <b-modal
      id="confirm-raise-bid-modal"
      ref="confirm-raise-bid-modal"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      :title="selectedSuggestion ? selectedSuggestion.title : 'Confirm Bid Change'"
      button-size="sm"
      @hide="onSuggestionHide"
      @hidden="onSuggestionHidden"
    >
      <p
        v-if="selectedBidRaise"
        class="mb-2"
        data-cy="text-confirm-suggestion"
      >
        <template v-if="selectedSuggestion.isMinimumOnly">
          Keep the bid on <strong>{{ selectedBidRaiseCampaign.name }}</strong> at
          <strong>{{ formatCurrency(selectedBidRaiseCampaign.budget.maxBidPerCallInUSD) }}</strong> and change its minimum from
          <strong>{{ selectedSuggestion.currentMinimumSeconds }}</strong> to
          <strong>{{ selectedSuggestion.minimumSeconds }} seconds</strong>?
        </template>
        <template v-else-if="selectedSuggestion.changesMinimum && selectedSuggestion.currentMinimumSeconds !== null">
          Raise the bid on <strong>{{ selectedBidRaiseCampaign.name }}</strong> from
          <strong>{{ formatCurrency(selectedBidRaiseCampaign.budget.maxBidPerCallInUSD) }}</strong> to
          <strong>{{ formatCurrency(selectedBidRaise.suggestedBidInUSD) }}</strong> and change its minimum from
          <strong>{{ selectedSuggestion.currentMinimumSeconds }}</strong> to
          <strong>{{ selectedSuggestion.minimumSeconds }} seconds</strong>?
        </template>
        <template v-else-if="selectedSuggestion.changesMinimum">
          Raise the bid on <strong>{{ selectedBidRaiseCampaign.name }}</strong> from
          <strong>{{ formatCurrency(selectedBidRaiseCampaign.budget.maxBidPerCallInUSD) }}</strong> to
          <strong>{{ formatCurrency(selectedBidRaise.suggestedBidInUSD) }}</strong> and set its minimum to
          <strong>{{ selectedSuggestion.minimumSeconds }} seconds</strong>?
        </template>
        <template v-else>
          Raise the bid on <strong>{{ selectedBidRaiseCampaign.name }}</strong> from
          <strong>{{ formatCurrency(selectedBidRaiseCampaign.budget.maxBidPerCallInUSD) }}</strong> to
          <strong>{{ formatCurrency(selectedBidRaise.suggestedBidInUSD) }}</strong>?
        </template>
        {{ selectedSuggestion.costText }}
      </p>

      <p
        v-if="selectedSuggestion && selectedSuggestion.countsText"
        class="mb-2"
        data-cy="text-confirm-counts"
      >
        {{ selectedSuggestion.countsText }}
      </p>

      <p
        v-if="selectedSuggestion && selectedSuggestion.reasonText"
        class="mb-0 text-muted"
        data-cy="text-confirm-minimum-reason"
      >
        {{ selectedSuggestion.reasonText }}
      </p>

      <template #modal-footer>
        <b-button
          type="button"
          variant="secondary"
          size="sm"
          :disabled="isLoading"
          @click="cancelSuggestion"
        >
          Cancel
        </b-button>

        <BaseButtonLoader
          :loading="isLoading"
          class="ml-2"
          type="button"
          :custom-button="false"
          size="sm"
          variant="primary"
          data-cy="button-confirm-raise-bid"
          @click="applySuggestedBid"
        >
          {{ selectedSuggestion ? selectedSuggestion.confirmLabel : 'Raise Bid' }}
        </BaseButtonLoader>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import CampaignBidInput from '@/components/buyer/dashboard/CampaignBidInput.vue'
import MarketPositionIndicator from './MarketPositionIndicator.vue'
import BidOpportunityCell from './BidOpportunityCell.vue'
import DynamicBidTerms from './DynamicBidTerms.vue'
import MissedCallsBanner from './MissedCallsBanner.vue'
import TouchTable from '@/components/common/TouchTable.vue'
import ReportingCardStats from '@/components/common/ReportingCardStats.vue'
import { CHANNEL_COLORS } from '@/utils/channelColors'

import { mapFeatureFlags } from '@/plugins/featureFlags'
import formatCurrency from '@/utils/formatCurrency'
import escapeHtml from '@/utils/escapeHtml'
import { FEATURE_FLAG_KEYS } from '@/utils/featureFlagKeys'
import { BID_SOURCES, USAGE_EVENTS, usageRoutingOf } from '@/utils/usageEventKeys'
import { showErrorMessage, showSuccessMessage, showWarningMessage } from '@/notification-utils'
import { bidChangeSources, canSoftphoneCampaignGoLive, getBadgeVariantForCampaignStatus, getCampaignRoutingLabel, getSoftphoneGoLiveBlockedReason, getStatusText, isSoftphoneCampaign, ACTIVE_STATUS, PAUSED_STATUS, campaignRoutingTypes } from '@/store/modules/campaign.store'
import { getMinBidMessage, getStaticCampaignMinBid } from '@/utils/campaignMinBid'
import {
  bidTiers,
  formatMarketWindow,
  getBidColumnTerms,
  getConfiguredMinimumSeconds,
  getMarketPositionTooltip,
  getMinimumCostText,
  getMinimumReasonText,
  getMissedAuctionsText,
  getOpportunityKind,
  getOpportunityTooltip,
  opportunityKinds,
  toCents
} from '@/store/modules/market-context.store'
import { BLOCKED_STATUSES, getSoftphoneStatusBadgeVariant, getSoftphoneStatusLabel, SOFTPHONE_STATUS } from '@/store/modules/softphone.store'

/**
 * The columns a softphone agent does not get. Every one of them is either fixed for all softphone
 * campaigns - US only, one static agent destination, one call type - or bookkeeping meant for the
 * account rather than the agent: the campaign's name is generated from the category and the
 * agent's own name, so the Category column beside it already says the only part that varies.
 */
const COLUMNS_HIDDEN_FROM_AGENTS = Object.freeze([
  'name',
  'country.name',
  'routing.type',
  'trafficSources.callType'
])

// The market columns, which go only where `showMarketContext` does: never to a softphone agent (see
// `marketContext/canViewMarketContext`). A separate list from the one above because the reason is
// separate: those say the same thing on every softphone row, these are market data a softphone
// agent is not shown at all.
const MARKET_COLUMNS = Object.freeze(['marketPosition', 'missedCalls', 'opportunity'])

// The RPC, Calls and Total (today) columns, which go only where `showLiveRpc` does (rx-campaign-live-rpc)
const LIVE_RPC_COLUMNS = Object.freeze(['liveRpc', 'liveCalls', 'liveRevenue'])

// How often the revenue per call refreshes while the list is open and the tab visible. The
// server reads Ringba at most about once a minute for everyone, so this only picks up its snapshot.
export const LIVE_RPC_POLL_INTERVAL_MS = 30000

// What a campaign, or the account, with no ended calls today shows instead of a revenue per call
export const LIVE_RPC_EMPTY = '\u2014'

// Icon colours of the cards above the table, from the palette Call Logs uses for its cards
const CAMPAIGN_RPC_ICON_COLORS = Object.freeze({
  // A muted yellow, gold for the coins, as soft as the palette's other colours on the dark theme
  rpc: '#e3b341',
  calls: CHANNEL_COLORS[5], // cyan
  revenue: CHANNEL_COLORS[0], // blue
  // A muted green, as Call Logs uses for sales: active reads as green
  active: '#5fb96a'
})

// A campaign that can take calls now: status Active and not paused
export function isActiveCampaign(campaign) {
  return campaign?.status === ACTIVE_STATUS && !campaign.isPaused
}

// The table's sort-compare. Only the RPC, Calls and Total (today) columns are handled: a null value
// (shown as a dash: no calls today for the RPC, no data yet or Ringba unreadable for all three) goes
// last whichever way it is sorted, numbers compare numerically. b-table multiplies the result by -1
// for a descending sort, so a null side answers by direction. Every other column returns null,
// which hands it back to b-table's own comparison unchanged.
export function compareLiveRpcForTable(a, b, key, sortDesc) {
  if (!LIVE_RPC_COLUMNS.includes(key)) {
    return null
  }

  const left = a[key] ?? null
  const right = b[key] ?? null

  if (left === right) {
    return 0
  }

  if (left === null) {
    return sortDesc ? -1 : 1
  }

  if (right === null) {
    return sortDesc ? 1 : -1
  }

  return left - right
}

// What a chip's suggestion changes on the campaign as the store has it, which the confirmation's
// wording and its usage events both read: a minimum-only change keeps the bid and shortens a
// minimum the campaign has (I19)
function getSuggestionChange(raise, campaign) {
  const currentMinimumSeconds = getConfiguredMinimumSeconds(campaign)
  const changesMinimum = raise.changesMinimum === true && raise.minimumSeconds != null
  const keepsBid = changesMinimum && toCents(raise.suggestedBidInUSD) <= toCents(campaign.budget.maxBidPerCallInUSD)

  return { changesMinimum, isMinimumOnly: keepsBid && currentMinimumSeconds !== null, currentMinimumSeconds }
}

// The market tooltips the usage events count, by their target's data-cy: the three headers' icons,
// and the hovers on a row's chip, advice, conversion advice and own-bid line
// (docs/MARKET_CONTEXT_USAGE_ANALYTICS.md). A row with no suggestion has no hover since R66
const MARKET_TOOLTIPS = Object.freeze({
  'tooltip-market-position': 'market_position_header',
  'tooltip-missed-calls': 'missed_header',
  'tooltip-opportunity': 'opportunity_header',
  'button-apply-suggested-setup': 'chip',
  'button-apply-suggested-bid': 'chip',
  'text-suggested-setup-advice': 'advice',
  'text-conversion-advice': 'conversion_advice',
  'text-own-bid': 'own_bid'
})

// A short grace period after the agent goes offline before Go Live is offered again: the widget
// pauses the campaign as it shuts down, and going live again inside that window races the pause.
const GO_LIVE_COOLDOWN_MS = 1000

// How long the softphone is given to register before the agent is told it has not connected.
// Generous on purpose: it fetches a WebRTC token, opens a websocket and registers - and when the
// softphone is running in another tab, all of that happens over there.
const SOFTPHONE_CONNECT_TIMEOUT_MS = 30000

// A failed go-live resets the widget to offline first and sets the reason a mutation later. This is
// how long the offline is given to be followed by that reason before the agent is told without one.
const SOFTPHONE_FAILURE_REASON_GRACE_MS = 500

// The agent is taking calls in every one of these - READY is the usual one, the rest cover a call
// that arrives before the mirror has seen the widget go ready.
const CONNECTED_STATUSES = Object.freeze([
  SOFTPHONE_STATUS.READY,
  SOFTPHONE_STATUS.RINGING,
  SOFTPHONE_STATUS.ACTIVE,
  SOFTPHONE_STATUS.WRAP_UP,
  /**
   * The blocked statuses are CONNECTED. The registration landed and the go-live worked; the account
   * merely cannot buy what the registration would answer, and the widget says so itself.
   *
   * Left out, they are not a wrong label but a wedge: registration commits `SET_STATUS(READY)`,
   * which resolves to a blocked status when the account is capped, so this wait would match nothing
   * and sit until its 30 second timeout - warning the agent that they are "not connected yet" when
   * they are, and holding `isTogglingSoftphone` for all 30 seconds. That flag is the FIRST branch of
   * `isSoftphoneButtonDisabled`, so it would also kill Go Offline on the row they just went live for.
   *
   * Reachable despite the check in `goLiveForCampaign`: the account only has to be capped in the
   * seconds between that check and the registration landing.
   */
  ...BLOCKED_STATUSES
])

export default {
  name: 'ListBuyerCampaigns',

  title: 'Campaigns',

  components: {
    CampaignBidInput,
    MarketPositionIndicator,
    BidOpportunityCell,
    DynamicBidTerms,
    MissedCallsBanner,
    ReportingCardStats,
    TouchTable
  },

  data() {
    return {
      loading: true,
      showLoadingError: false,
      loadingErrorText: '',
      selectedBuyerCampaign: null,
      selectedBidRaise: null,
      isLoading: false,
      isTogglingSoftphone: false,
      isGoLiveCoolingDown: false,
      goLiveCooldownTimer: null,
      sortBy: '',
      sortDesc: false,
      // The campaigns and the SOFTPHONE_GOES_LIVE setting have both loaded. Stays false when the
      // campaigns fail to load, so no market fetch runs under a load error.
      campaignsLoaded: false,
      marketPositionsRefreshTimeout: null,
      // Inline bid saves not yet confirmed to the buyer, by campaign id: { name, bidInUSD }. Each
      // burst of saves on a row gets one toast, with the last bid, on the market refetch debounce
      pendingInlineBidToasts: {},
      liveRpcPollTimer: null,
      // A campaign RPC request is on its way; the next tick waits for it rather than superseding it
      liveRpcRefreshInFlight: false,
      CAMPAIGN_RPC_ICON_COLORS,
      // How the confirmation was closed, for its `hidden` handler, whose event carries none: Cancel
      // sets it just before it closes it, which BootstrapVue reports as any other programmatic hide,
      // and the `hide` event sets the header's ×, Esc or the backdrop
      suggestionDismissTrigger: null,
      // The open confirmation's Confirm was clicked, whatever came of it, so closing it after a
      // failed write is not a dismissal: each opening is reported confirmed or dismissed, not both
      suggestionConfirmTried: false
    }
  },

  computed: {
    ...mapState('campaign', [
      'buyerCampaigns'
    ]),

    ...mapGetters('marketContext', [
      'getMarketPositionForCampaign',
      'campaignMarketPositionsWindowHours',
      'campaignMarketPositionsHavePositionWindow',
      'campaignMarketPositionsTruncated',
      'campaignMarketPositionsLimit',
      'canViewMarketContext'
    ]),

    ...mapFeatureFlags({
      marketIntelligenceEnabled: FEATURE_FLAG_KEYS.marketIntelligence.key,
      campaignLiveRpcEnabled: FEATURE_FLAG_KEYS.campaignLiveRpc.key
    }),

    ...mapGetters('campaign', ['getLiveRpcForCampaign', 'liveRpcTotal', 'liveRpcUnavailable']),

    // The revenue per call column, the "RPC today" header total and their polling: once the list
    // has loaded, inside the rx-campaign-live-rpc rollout, for everyone the list is shown to. There is
    // no audience rule: softphone agents see both like any user, because agents are not separated
    // inside an account today (they already see all of its campaigns and call logs). The flag is read
    // last: its first read logs a Datadog exposure.
    showLiveRpc() {
      return this.campaignsLoaded && this.campaignLiveRpcEnabled
    },

    // The figures on the RPC today, Calls today and Total today cards, over all of the account's
    // campaigns. Dashes until the first response, and dashes with "unavailable" under the RPC when the
    // server could not read Ringba (its zeros are placeholders then, not a quiet day). The RPC is a
    // dash too on a day with no ended calls yet.
    campaignRpcCards() {
      const total = this.liveRpcTotal

      if (!total || this.liveRpcUnavailable) {
        return {
          rpc: LIVE_RPC_EMPTY,
          rpcNote: total ? 'unavailable' : '',
          calls: LIVE_RPC_EMPTY,
          revenue: LIVE_RPC_EMPTY
        }
      }

      return {
        rpc: this.formatLiveRpc(total.rpc),
        rpcNote: '',
        calls: Number(total.calls || 0).toLocaleString('en-US'),
        revenue: formatCurrency(Number(total.revenue || 0))
      }
    },

    // Campaigns in this list that can take calls now: active and not paused
    activeCampaignsCount() {
      return this.visibleCampaigns.filter(campaign => isActiveCampaign(campaign)).length
    },

    // Market columns, banner and the positions behind them: once the campaigns and the softphone
    // setting have loaded (canViewMarketContext fails open until then), for anyone it allows (not a
    // softphone agent).
    showMarketContext() {
      return this.campaignsLoaded && this.canViewMarketContext
    },

    // Slice H's suggestions for existing campaigns (market intelligence, iteration 1, §4.6): the
    // chips and the advice, the own-bid line with the minimum, the confirmations, and the banner and
    // tooltips worded in auctions. Inside the market columns only, so rx-market-intelligence is read
    // last, only where the columns already show: never for an agent, nor before the list loads.
    showMarketIntelligence() {
      return this.showMarketContext && this.marketIntelligenceEnabled
    },

    ...mapGetters('globalSettings', ['isSoftphoneUser', 'canViewSoftphoneCampaigns']),

    ...mapGetters('auth', ['userId', 'isImpersonating']),

    ...mapGetters('softphone', [
      'isSoftphoneAvailable',
      'liveCampaignId',
      'softphoneStatusLabel'
    ]),

    ...mapState('softphone', {
      softphoneStatus: state => state.status
    }),

    ...mapState('account', ['accountUsers']),

    // The buyer balance endpoint's answer, refreshed every 45 seconds by the header - see
    // AccountSummaryHeader. What the Go Live button reads to know whether a shift started now
    // could ring at all.
    ...mapState('billing', ['accountBalance']),

    // The window every market number in this table was measured over - 24h on six days of the
    // week, 7d on Mondays - as { short, long } label strings. Both are EMPTY until
    // fetchCampaignMarketPositions resolves, so the labels render bare rather than showing
    // "24h" and then flipping to "7d" a moment later
    marketWindow() {
      return formatMarketWindow(this.campaignMarketPositionsWindowHours)
    },

    // "7 days" or "24 hours" under the Missed header, empty until the window is known
    missedCallsWindowText() {
      return this.marketWindow.long.replace(/^last /, '')
    },

    // Lives in computed, not data, only because the Missed column's label is data-driven now.
    // Object.freeze stays INSIDE the computed on purpose: it stops BootstrapVue's field
    // normalisation mutating these source objects, which is a correctness guard rather than a
    // micro-optimisation. The formatter arrows below resolve `this` identically here and in
    // data(), since statusText is a method and startCase comes from the global mixin
    fields() {
      return Object.freeze([
        {
          key: 'status',
          sortable: true,
          // b-table sorts on `item.status`, which is the server's word and not the one in the cell
          // - an agent clicking the header would reorder a column of "Offline" badges by a value
          // nobody can see. Sorting on the rendered text is the only sort that looks like one.
          formatter: (value, key, item) => this.statusText(item),
          sortByFormatted: true,
          thClass: 'text-center',
          tdClass: 'text-center'
        },
        {
          key: 'name',
          sortable: true,
        },
        {
          key: 'category.name',
          label: 'Category',
          sortable: true,
        },
        {
          key: 'country.name',
          label: 'Country',
          sortable: true,
        },
        {
          key: 'language.name',
          label: 'Language',
          sortable: true,
        },
        {
          key: 'routing.type',
          label: 'Routing',
          sortable: true,
          formatter: (value, key, item) => {
            return getCampaignRoutingLabel(item, this.startCase)
          }
        },
        {
          key: 'trafficSources.callType',
          label: 'Call Type',
          sortable: true
        },
        {
          key: 'maxBidPerCallInUSD',
          label: 'Bid',
          sortable: true,
          // The bid is on `item.budget`, so b-table's own lookup of this key found nothing and the
          // column never sorted. A dynamic campaign sorts by the bid its cell shows (R65), and one
          // that reads "Dynamic" sorts with the others that do, rather than by a number the cell
          // doesn't show.
          formatter: (value, key, item) => {
            if (this.isStaticCampaign(item)) {
              return Number(item.budget?.maxBidPerCallInUSD)
            }

            return this.getBidColumnTermsFor(item)?.bidInUSD ?? null
          },
          sortByFormatted: true,
          thClass: 'text-right',
          tdClass: 'text-right',
        },
        {
          key: 'dailyBudgetInUSD',
          label: 'Daily Budget',
          sortable: true,
          // On `item.budget` too, the same as the bid
          formatter: (value, key, item) => Number(item.budget?.dailyBudgetInUSD),
          sortByFormatted: true,
          thClass: 'text-right',
          tdClass: 'text-right',
        },
        {
          key: 'marketPosition',
          label: 'Market Position',
        },
        {
          key: 'missedCalls',
          label: this.marketWindow.short ? `Missed (${this.marketWindow.short})` : 'Missed',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right',
        },
        {
          key: 'opportunity',
          label: 'Opportunity',
        },
        {
          key: 'liveRpc',
          label: 'RPC',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right',
        },
        {
          key: 'liveCalls',
          label: 'Calls',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right',
        },
        {
          key: 'liveRevenue',
          label: 'Total',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right',
        },
        {
          key: 'actions',
          tdAttr: {
            'data-cy': 'table-column-actions'
          }
        }
      ])
    },

    // Whether anyone on this account actually takes softphone calls. Read off the account's user
    // list (`softphoneAgent` per row - the same field admin-ui toggles) rather than off the
    // campaigns, so the note still appears for an account whose agents have not created a campaign
    // yet, which is exactly when "you cannot create one for them" is worth saying.
    accountHasSoftphoneAgents() {
      return (this.accountUsers ?? []).some(user => user?.softphoneAgent === true)
    },

    // Staff only, and only where it applies: an impersonating super-admin looking at an account
    // that does softphone work. A regular user never sees it, and neither does an admin on an
    // account with no agents - there would be nothing for the note to be about.
    showSoftphoneAdminNotice() {
      return this.isImpersonating
        && this.canViewSoftphoneCampaigns
        && this.accountHasSoftphoneAgents
    },

    // The list is partitioned by user type rather than filtered: a softphone user sees only
    // softphone campaigns, everyone else sees only the standard ones. Campaigns stay on the
    // account either way - this only controls what the signed-in user is shown.
    //
    // A super-admin impersonating the account is the exception and reads it whole. The partition
    // exists because a softphone user has no page for a standard campaign and vice versa; staff
    // can open either edit page, and partitioning them would instead hide half the account from
    // the one person looking at it on the account's behalf.
    visibleCampaigns() {
      if (this.isImpersonating && this.canViewSoftphoneCampaigns) {
        return this.buyerCampaigns
      }

      return this.buyerCampaigns.filter(
        campaign => isSoftphoneCampaign(campaign) === this.isSoftphoneUser
      )
    },

    // Two filters, each for its own reason. First the market columns, for whoever is shown no
    // market data - see MARKET_COLUMNS. Then the same partition as the rows: a softphone agent
    // reads a shorter table - see COLUMNS_HIDDEN_FROM_AGENTS.
    //
    // Staff keep every column, including staff who are softphone agents in their own right. The
    // reduced table is only coherent alongside the agent's reduced ROW set: it drops Name, and an
    // admin is shown the account's standard campaigns too, which are then unidentifiable.
    visibleFields() {
      const marketFields = this.showMarketContext
        ? this.fields
        : this.fields.filter(field => !MARKET_COLUMNS.includes(field.key))

      const fields = this.showLiveRpc
        ? marketFields
        : marketFields.filter(field => !LIVE_RPC_COLUMNS.includes(field.key))

      if (!this.isSoftphoneUser || this.isImpersonating) {
        return fields
      }

      return fields.filter(field => !COLUMNS_HIDDEN_FROM_AGENTS.includes(field.key))
    },

    // Merges the market position onto each campaign row so the b-table can sort
    // on missedCalls natively. The endpoint returns a row for every campaign
    // whatever its routing type, so a null here means only that the campaign's market
    // cell had no auctions in the window, or that the fetch has not resolved yet -
    // never that the campaign is the wrong routing type. Those rows render as a dash.
    campaignsWithMarketData() {
      return this.visibleCampaigns.map(campaign => {
        const marketPosition = this.getMarketPositionForCampaign(campaign.id)
        const liveRpc = this.showLiveRpc ? this.getLiveRpcForCampaign(campaign.id) : null
        // The server's zeros are placeholders when it could not read Ringba, not a quiet day
        const liveFigures = liveRpc && !this.liveRpcUnavailable ? liveRpc : null

        return {
          ...campaign,
          marketPosition,
          missedCalls: marketPosition ? marketPosition.missedCalls : null,
          // Today's revenue per call, null with no calls today or before the first fetch
          liveRpc: liveRpc ? liveRpc.rpc : null,
          // Today's ended calls and revenue (0 on a quiet day), null before the first fetch and when
          // Ringba could not be read
          liveCalls: liveFigures ? Number(liveFigures.calls || 0) : null,
          liveRevenue: liveFigures ? Number(liveFigures.revenue || 0) : null
        }
      })
    },

    // Feeds the missed-calls banner. Routing type is deliberately not part of the filter:
    // a dynamic campaign whose median observed bid lands below market is genuinely
    // below market and its missed calls are real, so they count toward the banner's
    // total. A dynamic campaign that placed no bids carries tier: null and falls out
    // here on its own, with no extra check. So does a bid with too few delivered calls from other
    // buyers to position it (tier: null with a positioned bid; R55, R57): it is not below market.
    belowMarketCampaigns() {
      return this.campaignsWithMarketData.filter(campaign => {
        return campaign.marketPosition
          && !this.isCampaignPaused(campaign)
          && (campaign.marketPosition.tier === bidTiers.BelowMarket || campaign.marketPosition.tier === bidTiers.PricedOut)
      })
    },

    // The two market columns explain themselves on hover, same pattern as missedCallsTooltip
    // below. Market Position names the prices the badge is scored against; the rows can mix E1's
    // window with the 7-day fallback (§3.7), so the header names both once the rows say they can
    // (I38). Opportunity names what its cells hold, a suggestion, conversion advice or why there is
    // none, with no window: each cell's hover gives its own (R60, which retired the rank band this
    // header used to explain)
    marketPositionTooltip() {
      return getMarketPositionTooltip(this.marketWindow, {
        withSevenDayFallback: this.campaignMarketPositionsHavePositionWindow
      })
    },

    opportunityTooltip() {
      return getOpportunityTooltip()
    },

    // Spells out what the column counts, since "Missed" on its own reads like every call
    // the campaign did not get. The count is AUCTIONS the campaign bid in and lost to a higher bid,
    // whether or not a call came in: gating it on a call having arrived was tried and reverted on
    // 2026-09-04, because non-delivery depends on price (docs/MARKET_CONTEXT_DELIVERED_CALLS.md).
    // Inside the rx-market-intelligence rollout it says so, in the wording approved with Slice H's
    // counts (I26; R41); outside it keeps today's "Calls". From backend PR 2 it leaves out auctions
    // the account's own campaigns won (R44). The window phrase is dropped until the fetch resolves,
    // for the same reason the column label drops its suffix
    missedCallsTooltip() {
      const windowPhrase = this.marketWindow.long ? ` in the ${this.marketWindow.long}` : ''
      const counted = this.showMarketIntelligence ? 'Auctions this campaign bid in' : 'Calls this campaign bid on'

      return `${counted} and lost to a higher bidder${windowPhrase}.`
        + ' Auctions it never entered are not counted.'
    },

    // The cap applies to the account's market cells and to its bidding campaigns, both of which
    // only outnumber it when the campaigns do - hence "campaigns"
    truncatedMarketPositionsText() {
      const cap = this.campaignMarketPositionsLimit
        ? ` (${this.campaignMarketPositionsLimit.toLocaleString('en-US')})`
        : ''

      // The banner's total counts auctions, and says so inside the rx-market-intelligence rollout
      const total = this.showMarketIntelligence ? 'missed-auctions total' : 'missed-calls total'

      return `This account has more campaigns than market data covers at once${cap}.`
        + ` Some campaigns are missing market data, and the ${total} leaves them out.`
    },

    // The row as the store has it NOW, not as it was when its raise chip was clicked: the modal
    // quotes this bid, and applySuggestedBid checks the raise against it
    selectedBidRaiseCampaign() {
      if (!this.selectedBidRaise) {
        return null
      }

      return this.buyerCampaigns.find(campaign => campaign.id === this.selectedBidRaise.campaign.id)
        || this.selectedBidRaise.campaign
    },

    // The open confirmation's suggestion, read against the campaign as the store has it now, as
    // selectedBidRaiseCampaign quotes its bid. Today's chip opens it with a raise; Slice H's switch
    // chip with a change of minimum too, which keeps the bid when only the minimum should change
    // (I19). Everything the modal says beyond the question is here:
    // - the direction of cost when the minimum changes (I18), and the minimum's reason (I14), left
    //   out without a bucket, as when the category's longest minimum capped the standard (I6);
    // - the row's Missed count, which the chip hands over, in the Missed column's window: "It lost
    //   7,244 auctions to higher bids from other buyers in the last 24 hours." Left out at 0, and for
    //   today's chip, which hands none (R54). It replaces R41's contested counts and R45's sentence.
    selectedSuggestion() {
      const raise = this.selectedBidRaise

      if (!raise) {
        return null
      }

      const { changesMinimum, isMinimumOnly, currentMinimumSeconds } = getSuggestionChange(raise, this.selectedBidRaiseCampaign)

      let title = 'Confirm Bid Change'
      let confirmLabel = 'Raise Bid'

      if (isMinimumOnly) {
        title = 'Confirm Minimum Change'
        confirmLabel = 'Change Minimum'
      } else if (changesMinimum) {
        title = 'Confirm Bid and Minimum Change'
        confirmLabel = 'Change Bid and Minimum'
      }

      return {
        changesMinimum,
        isMinimumOnly,
        currentMinimumSeconds,
        minimumSeconds: raise.minimumSeconds,
        title,
        confirmLabel,
        costText: changesMinimum ? getMinimumCostText(currentMinimumSeconds, raise.minimumSeconds) : '',
        reasonText: changesMinimum ? getMinimumReasonText(raise.minimumBucket) : '',
        countsText: getMissedAuctionsText(raise.missedCalls, this.marketWindow, { subject: 'It' })
      }
    },

    totalMissedCalls() {
      return this.belowMarketCampaigns.reduce((total, campaign) => total + (campaign.missedCalls || 0), 0)
    },

    campaignsBelowMarketCount() {
      return this.belowMarketCampaigns.length
    },

    // MissedCallsBanner shows: inside the market columns, with missed calls to report
    showsMissedCallsBanner() {
      return this.showMarketContext && this.totalMissedCalls > 0
    }
  },

  watch: {
    // Covers every way the agent stops being live - the Go Offline button, the owning tab closing,
    // or another tab taking over - because all of them land here by clearing the live id.
    liveCampaignId(newCampaignId, oldCampaignId) {
      this.hideOpenTooltips()

      if (oldCampaignId && !newCampaignId) {
        this.startGoLiveCooldown()
      }
    },

    // The click disables the button, and a disabled button never receives the mouseleave that
    // would close its tooltip - so the text sat there until the page was clicked elsewhere.
    isTogglingSoftphone() {
      this.hideOpenTooltips()
    },

    // The one place the revenue per call polling starts and stops: when the list finishes
    // loading inside the rollout, when Datadog's answer turns the flag on later, and when it turns
    // off in an open tab.
    showLiveRpc(show) {
      if (show) {
        this.startLiveRpcPolling()

        return
      }

      this.stopLiveRpcPolling()

      if (LIVE_RPC_COLUMNS.includes(this.sortBy)) {
        this.sortBy = ''
        this.sortDesc = false
      }
    },

    // The banner's usage event, once per load of the list. The list sends it rather than the banner,
    // which goes and comes back with the market columns
    showsMissedCallsBanner: {
      immediate: true,
      handler(shows) {
        if (shows) {
          this.$trackOnce(USAGE_EVENTS.marketBannerShown.name, 'load', { intelligence: this.showMarketIntelligence })
        }
      }
    },

    // The one place the market positions are fetched on load: when the list finishes loading.
    // Turning false (a logout) takes the market sort and the raise-bid modal with the columns.
    showMarketContext(show) {
      if (show) {
        this.loadCampaignMarketPositions()

        return
      }

      // BootstrapVue keeps sorting by a key whose column has gone, which would leave the rows in
      // missed-calls order with nothing on screen to say so.
      if (MARKET_COLUMNS.includes(this.sortBy)) {
        this.sortBy = ''
        this.sortDesc = false
      }

      this.selectedBidRaise = null
      this.$bvModal.hide('confirm-raise-bid-modal')
    },

    // rx-market-intelligence turning off in an open tab takes Slice H's chips with it, and a
    // confirmation one of them opened. Today's chip stays, and so does its confirmation.
    showMarketIntelligence(show) {
      if (show || !this.selectedBidRaise?.fromSuggestedSetup) {
        return
      }

      this.selectedBidRaise = null
      this.$bvModal.hide('confirm-raise-bid-modal')
    }
  },

  async created () {
    try {
      this.loading = true
      await Promise.all([
        this.fetchBuyerCampaigns(),
        this.getSoftphoneGoesLiveSetting()
      ])

      // `getBoolSetting` never rejects, so the setting, and with it the agent rule, is in by now.
      // This is what sets off the `showMarketContext` watcher.
      this.campaignsLoaded = true
    } catch (error) {
      if (error.response.status === 422) {
        this.showLoadingError = true
        this.loadingErrorText = error.response.data.title
      } else {
        showErrorMessage(error.response?.data?.title)
      }
    } finally {
      this.loading = false
    }

    // The market positions are not fetched here but by the `showMarketContext` watcher, so the
    // market query, which runs on ClickHouse and can be slow, does not hold up the staff note.
    await this.loadSoftphoneAgentPresence()
  },

  mounted() {
    this.$root.$on('bv::tooltip::shown', this.onTooltipShown)
  },

  beforeDestroy() {
    this.clearGoLiveCooldown()
    clearTimeout(this.marketPositionsRefreshTimeout)
    // A save that landed within the last second is still confirmed; only the refetch is dropped
    this.showInlineBidToasts()
    this.stopLiveRpcPolling()
    this.$root.$off('bv::tooltip::shown', this.onTooltipShown)
  },

  methods: {
    ...mapActions('campaign', [
      'fetchBuyerCampaigns',
      'updateCampaignBid',
      'updateCampaignBidAndMinimum'
    ]),

    ...mapActions('globalSettings', ['getSoftphoneGoesLiveSetting']),

    ...mapActions('account', ['getAccountUsers']),

    ...mapActions('billing', ['getBuyerAccountBalance']),

    ...mapActions('campaign', ['fetchBuyerCampaignBudgetState']),

    /**
     * The campaign's own daily budget, which the row in front of the agent cannot answer for: the
     * list endpoint returns the campaign ENTITY and the flag only exists on the DTO the
     * single-campaign endpoint builds. Without this the gate below was reading `undefined` and a
     * campaign that had spent its budget went on offering Go Live.
     *
     * Best effort, like the balance: null means the campaign's budget could not be read, which is
     * not evidence that it is spent.
     */
    async refreshCampaignBudgetBeforeGoingLive(campaign) {
      try {
        return await this.fetchBuyerCampaignBudgetState(campaign.id)
      } catch (error) {
        console.error('Could not re-check the campaign daily budget before going live', error)

        return null
      }
    },

    /**
     * Re-reads the balance so the go-live check below is about the account as it stands NOW.
     *
     * The button's disabled state is computed from a balance the header refreshes every 45 seconds,
     * and an account can run dry inside that window - which is precisely the click that would start
     * a silent shift. Going live is a deliberate, occasional act, so one request to be sure of it is
     * cheap.
     *
     * RETURNS the balance it fetched, and the caller gates on that rather than on the store, which
     * is not the same thing. Several places refresh the balance - this, the header's 45 second
     * poll, Add Funds, the billing pages - and two of them can be in flight at once, so
     * `SET_ACCOUNT_BALANCE` is last-writer-wins by arrival, not by recency. An older healthy
     * response landing after this one would put the account back over the line in the store, and
     * the gate would wave through the click this request was sent to refuse. The response in hand
     * cannot be overtaken.
     *
     * Best effort, and deliberately so: null means the balance could not be fetched, which is not
     * evidence that the account is broke. Refusing the shift over a failed request would be a
     * worse failure than the one this prevents, so the figure already in the store decides instead.
     */
    async refreshAccountBalanceBeforeGoingLive() {
      try {
        return await this.getBuyerAccountBalance()
      } catch (error) {
        console.error('Could not re-check the account balance before going live', error)

        return null
      }
    },

    /**
     * Loads the market positions behind the market columns and banner, for everyone who is shown
     * them (`showMarketContext`). Nothing is fetched for a softphone agent (see
     * `marketContext/canViewMarketContext`).
     *
     * Market context is a helper, never a blocker - the campaigns table must render even when this
     * fetch fails, so it stays out of `created`'s loading try/catch.
     */
    async loadCampaignMarketPositions() {
      if (!this.showMarketContext) {
        return
      }

      try {
        await this.fetchCampaignMarketPositions()
      } catch (error) {
        showErrorMessage(error.response?.data?.title || 'Failed to load market positions')

        return
      }

      this.trackMarketListShown()
    },

    // The usage event once the positions are in, once per load: a refetch after an edit does not
    // count again, and does not count the rows either. Rows are counted by what their Opportunity
    // cell shows
    trackMarketListShown() {
      this.$trackOnce(USAGE_EVENTS.marketListShown.name, 'load', () => {
        const counts = {}

        for (const campaign of this.campaignsWithMarketData) {
          const kind = this.getRowOpportunityKind(campaign)

          counts[kind] = (counts[kind] || 0) + 1
        }

        const chips = counts[opportunityKinds.RaiseChip] || 0
        const switches = counts[opportunityKinds.SwitchChip] || 0
        const legacy = counts[opportunityKinds.LegacyChip] || 0
        const advice = counts[opportunityKinds.Advice] || 0
        const conversionAdvice = counts[opportunityKinds.ConversionAdvice] || 0
        const noSuggestion = counts[opportunityKinds.NoSuggestion] || 0

        return {
          intelligence: this.showMarketIntelligence,
          rowsWithRaiseChip: chips,
          rowsWithSwitchChip: switches,
          rowsWithLegacyChip: legacy,
          rowsWithAdvice: advice,
          rowsWithConversionAdvice: conversionAdvice,
          rowsWithNoSuggestionReason: noSuggestion,
          rowsWithNone: this.campaignsWithMarketData.length - chips - switches - legacy - advice - conversionAdvice - noSuggestion
        }
      })
    },

    // What the row's Opportunity cell shows, from the props the template passes it
    getRowOpportunityKind(campaign) {
      return getOpportunityKind({
        campaign,
        marketPosition: campaign.marketPosition,
        isStatic: this.isStaticCampaign(campaign),
        marketIntelligence: this.showMarketIntelligence,
        routingType: this.getRoutingType(campaign),
        isCpaOnly: this.isCpaOnlyCampaign(campaign),
        programmaticBidSettings: this.getProgrammaticBidSettings(campaign)
      })
    },

    /**
     * Loads the account's user list, but only for the one reader who needs it: an impersonating
     * admin, to decide whether the softphone note applies. Nobody else pays for the call.
     *
     * Kept out of `created`'s loading try/catch and swallowed on failure on purpose. This only decides
     * whether an informational alert renders, and the endpoint additionally requires the account
     * to have completed onboarding - a 403 there must not take the campaigns list down with it.
     */
    async loadSoftphoneAgentPresence() {
      if (!this.isImpersonating || !this.canViewSoftphoneCampaigns) {
        return
      }

      try {
        await this.getAccountUsers()
      } catch (error) {
        console.error('Could not read the account users to check for softphone agents', error)
      }
    },

    ...mapActions('campaign', {
      setResumeBuyerCampaignStatus: 'resumeBuyerCampaign',
      setPauseBuyerCampaignStatus: 'pauseBuyerCampaign',
      goLiveSoftphoneCampaign: 'goLiveSoftphoneCampaign'
    }),

    ...mapActions('marketContext', [
      'fetchCampaignMarketPositions'
    ]),

    ...mapActions('campaign', ['fetchBuyerCampaignLiveRpc']),

    // The table's sort-compare, for the template
    compareLiveRpcForTable,

    // Dollars and cents, a dash for none (the RPC and Total columns)
    formatLiveRpc(rpc) {
      return rpc == null ? LIVE_RPC_EMPTY : formatCurrency(Number(rpc))
    },

    // "1,234", a dash before the first fetch or when Ringba could not be read
    formatLiveCalls(calls) {
      return calls == null ? LIVE_RPC_EMPTY : Number(calls).toLocaleString('en-US')
    },

    /**
     * Fetches now and every LIVE_RPC_POLL_INTERVAL_MS while the tab is visible; a hidden tab skips its
     * ticks and fetches as soon as it is shown again (the BuyerCallLogs pattern). Idempotent.
     */
    startLiveRpcPolling() {
      if (this.liveRpcPollTimer) {
        return
      }

      this.refreshLiveRpc()
      this.liveRpcPollTimer = setInterval(() => this.refreshLiveRpc(), LIVE_RPC_POLL_INTERVAL_MS)
      document.addEventListener('visibilitychange', this.onLiveRpcVisibilityChange)
    },

    stopLiveRpcPolling() {
      clearInterval(this.liveRpcPollTimer)
      this.liveRpcPollTimer = null
      document.removeEventListener('visibilitychange', this.onLiveRpcVisibilityChange)
    },

    onLiveRpcVisibilityChange() {
      if (!document.hidden) {
        this.refreshLiveRpc()
      }
    },

    /**
     * A helper, never a blocker: a failed refresh keeps the last numbers on screen and the next tick
     * tries again, so it only logs.
     *
     * One request at a time. The store drops a response a newer request has overtaken, and the http
     * client waits up to 40 s, so without this a server answering in 35 s would have every answer
     * superseded by the next 30 s tick (or a tab coming back into view) and never shown.
     */
    async refreshLiveRpc() {
      if (!this.showLiveRpc || document.hidden || this.liveRpcRefreshInFlight) {
        return
      }

      this.liveRpcRefreshInFlight = true
      try {
        await this.fetchBuyerCampaignLiveRpc()
      } catch (error) {
        console.error('Could not refresh the revenue per call', error)
      } finally {
        this.liveRpcRefreshInFlight = false
      }
    },

    ...mapActions('softphone', [
      'requestGoOnline',
      'requestGoOffline'
    ]),

    getBadgeVariantForCampaignStatus,

    getStatusText,

    isSoftphoneCampaign,

    formatCurrency,

    isCampaignPaused(campaign) {
      return campaign.status === PAUSED_STATUS || campaign.isPaused
    },

    // A softphone campaign is paused and resumed by going offline and live - the agent has to be
    // connected to take calls, so pausing or resuming it on its own would desync the widget.
    canPauseCampaign(campaign) {
      return !isSoftphoneCampaign(campaign)
    },

    showConfirmPauseBuyerCampaign(campaign) {
      this.selectedBuyerCampaign = campaign

      this.$bvModal.show('confirm-pause-modal')
    },

    async pauseBuyerCampaign() {
      try {
        this.isLoading = true
        await this.setPauseBuyerCampaignStatus({ campaignId: this.selectedBuyerCampaign.id })

        showSuccessMessage('Successfully paused buyer campaign')

        this.selectedBuyerCampaign = null

        this.$bvModal.hide('confirm-pause-modal')
      } catch (error) {
        showErrorMessage(error.response?.data?.title)
      } finally {
        this.isLoading = false
      }
    },

    showConfirmResumeBuyerCampaign(campaign) {
      this.selectedBuyerCampaign = campaign

      this.$bvModal.show('confirm-resume-modal')
    },

    async resumeBuyerCampaign() {
      try {
        this.isLoading = true
        await this.setResumeBuyerCampaignStatus({ campaignId: this.selectedBuyerCampaign.id, status: this.selectedBuyerCampaign.status })

        showSuccessMessage('Successfully resumed buyer campaign')

        this.selectedBuyerCampaign = null

        this.$bvModal.hide('confirm-resume-modal')
      } catch (error) {
        showErrorMessage(error.response?.data?.title)
      } finally {
        this.isLoading = false
      }
    },

    // Closes any tooltip currently open anywhere on the page. Used whenever a button this list
    // owns is about to be disabled, relabelled or removed under the pointer.
    hideOpenTooltips() {
      this.$root.$emit('bv::hide::tooltip')
    },

    isLiveSoftphoneCampaign(campaign) {
      return this.liveCampaignId === campaign.id
    },

    /**
     * The Status column, in the agent's own vocabulary on their own campaigns.
     *
     * A softphone campaign is not something the agent starts and stops - it is the campaign their
     * widget is on, or one it is not - so the row says what the widget says rather than what the
     * server calls the campaign underneath. The campaign the widget holds mirrors it exactly, down
     * to Connecting and Wrap-up; every other softphone campaign reads Offline, because that is what
     * the agent's softphone is on it.
     *
     * Review and rejection are the exception, and stay as they are: those are about whether the
     * campaign may EVER take calls, which no softphone status can say, and the Go Live button is
     * dead on them for exactly that reason.
     */
    statusText(campaign) {
      if (!this.isAgentStatusRow(campaign)) {
        return getStatusText(campaign)
      }

      return this.isLiveSoftphoneCampaign(campaign)
        ? this.softphoneStatusLabel
        : getSoftphoneStatusLabel(SOFTPHONE_STATUS.OFFLINE)
    },

    statusVariant(campaign) {
      if (!this.isAgentStatusRow(campaign)) {
        return getBadgeVariantForCampaignStatus(campaign)
      }

      return getSoftphoneStatusBadgeVariant(
        this.isLiveSoftphoneCampaign(campaign) ? this.softphoneStatus : SOFTPHONE_STATUS.OFFLINE
      )
    },

    /**
     * Which rows get the widget's vocabulary: the ones whose Go Live button this agent's own
     * softphone is behind. Deliberately the same test the button itself uses - a row whose status
     * the softphone speaks for, but whose button is missing or dead, is the mismatch this whole
     * change exists to remove.
     *
     * The campaign the widget is live for is in whatever the campaign looks like now, exactly as
     * `showSoftphoneButton` keeps Go Offline on it: an administrator strict-pausing a campaign
     * mid-call must not flip the row to "Paused" while the widget reads "On call".
     *
     * In review, rejected, expired, deleted and strict-paused rows keep the server's own status.
     * Those say whether the campaign may EVER take calls, which no softphone status can, and an
     * agent told their campaign is merely "Offline" would keep pressing a button that cannot work.
     */
    isAgentStatusRow(campaign) {
      if (!isSoftphoneCampaign(campaign) || !this.isOwnSoftphoneCampaign(campaign)) {
        return false
      }

      if (this.isLiveSoftphoneCampaign(campaign)) {
        return true
      }

      return canSoftphoneCampaignGoLive(campaign) && !this.isStrictPausedCampaign(campaign)
    },

    /**
     * The list covers the WHOLE ACCOUNT, colleagues' softphone campaigns included - see
     * `getTakenSoftphoneCampaignCombos`, which has to filter by the same field for the same reason.
     * Another agent's campaign says nothing about this agent's softphone: one of theirs sitting
     * Active with them on a call must not read "Offline" here just because this widget is not on it.
     */
    isOwnSoftphoneCampaign(campaign) {
      return Boolean(this.userId) && campaign?.createdBy === this.userId
    },

    /**
     * Going live is offered only where it can work. Approval is what creates the target the calls
     * arrive through, and a strict pause can only be lifted by an administrator, so on those rows
     * the button was permanently dead - the status badge already says why, and a disabled button
     * next to it only invites clicking.
     *
     * The row the agent is live for keeps its button whatever the campaign looks like now: that
     * button is Go Offline, and it is the only way back off a campaign an administrator has since
     * strict-paused.
     */
    showSoftphoneButton(campaign) {
      // Never for staff impersonating the account, including staff who are softphone agents
      // elsewhere: the campaign rings the account's agent, not them. The backend refuses both
      // halves anyway - go-live requires the campaign's CreatedBy to be the caller, and the WebRTC
      // token requires the account be one of the caller's own memberships - so the button could
      // only ever have errored.
      if (this.isImpersonating || !this.isSoftphoneAvailable || !isSoftphoneCampaign(campaign)) {
        return false
      }

      if (this.isLiveSoftphoneCampaign(campaign)) {
        return true
      }

      return canSoftphoneCampaignGoLive(campaign) && !this.isStrictPausedCampaign(campaign)
    },

    // An administrator paused it and only an administrator can resume it. Going live resumes the
    // campaign first, and the resume endpoint rejects a strict pause, so offering the button would
    // only open a window and then fail.
    isStrictPausedCampaign(campaign) {
      return campaign?.isStrictPause === true
    },

    /**
     * Why a shift started right now could not ring, or null. See
     * `getSoftphoneGoLiveBlockedReason` - an empty balance or a spent daily budget, both of which
     * make Ringba cap the buyer and route nothing.
     *
     * Deliberately NOT asked of the row the agent is already live for. That button is Go Offline,
     * and it is the only way off the campaign: an account that ran dry mid-shift must not take the
     * way out with it.
     *
     * `accountBalance` defaults to the store's copy, which is what the disabled state of every
     * button in the list is computed from. The click passes the response it just fetched instead -
     * see `refreshAccountBalanceBeforeGoingLive` for why the two are not interchangeable.
     */
    softphoneGoLiveBlockedReason(campaign, accountBalance = this.accountBalance) {
      if (this.isLiveSoftphoneCampaign(campaign)) {
        return null
      }

      return getSoftphoneGoLiveBlockedReason(campaign, accountBalance)
    },

    // Once the agent is live for one campaign every other Go Live is dead: they have one pair of
    // ears, and a second campaign routing to the same widget would be a call nobody answers.
    isSoftphoneButtonDisabled(campaign) {
      if (this.isTogglingSoftphone || this.isGoLiveCoolingDown) {
        return true
      }

      if (this.isLiveSoftphoneCampaign(campaign)) {
        return false
      }

      return !!this.liveCampaignId
        || this.isStrictPausedCampaign(campaign)
        || !canSoftphoneCampaignGoLive(campaign)
        /**
         * No funds, or the day's budget already spent. The campaign is perfectly able to take
         * calls and none would be bought, so going live would register the agent into silence.
         *
         * ACCOUNT-level caps only, in practice. Those flags are already in the store from the
         * header's poll, so they cost nothing to read here - but a CAMPAIGN's own daily budget
         * lives on the single-campaign endpoint, and the list returns the entity, which has no such
         * field. Disabling the button for it would mean one request per row on every visit to this
         * page, for a state most rows are not in.
         *
         * So a campaign over its own daily budget keeps a live-looking button and is refused on the
         * CLICK instead - see `goLiveForCampaign`, which fetches the campaign's budget before it
         * commits the agent to anything. Deliberate: the agent never goes live into silence, the
         * button merely looks available until they try. Closing it properly wants the flag on the
         * list response, which is a server-side change.
         */
        || Boolean(this.softphoneGoLiveBlockedReason(campaign))
    },

    softphoneButtonTitle(campaign) {
      if (this.isLiveSoftphoneCampaign(campaign)) {
        return 'Stop taking calls and pause this campaign'
      }

      if (this.isStrictPausedCampaign(campaign)) {
        return campaign.strictPauseReason
          ? `Paused by an administrator: ${campaign.strictPauseReason}`
          : 'Paused by an administrator and can only be resumed by one'
      }

      // Approval is what creates the target the calls arrive through, so an unapproved campaign
      // cannot ring however live the agent is. Said here rather than left to the go-live refusal:
      // the status badge alone does not explain why the button does nothing.
      if (!canSoftphoneCampaignGoLive(campaign)) {
        return campaign.status === 'InReview'
          ? 'Waiting for approval - you can go live once this campaign is approved'
          : 'This campaign cannot take calls'
      }

      if (this.isGoLiveCoolingDown) {
        return 'Finishing up your last session'
      }

      // The agent's own click is what disabled this button, and the go-live it started may still
      // be in flight - telling them they are live for ANOTHER campaign reads as a refusal of the
      // one they just clicked.
      if (this.isTogglingSoftphone) {
        return 'Already live'
      }

      /**
       * Ahead of "already live for another campaign", which is the generic below it: an account
       * that cannot buy calls is the bigger fact and the one the agent has to pass on to whoever
       * holds the card. Being live elsewhere is something they can see for themselves in their own
       * widget.
       */
      const blockedReason = this.softphoneGoLiveBlockedReason(campaign)

      if (blockedReason) {
        return blockedReason
      }

      if (this.isSoftphoneButtonDisabled(campaign)) {
        return 'You are already live for another campaign'
      }

      return 'Start taking calls for this campaign in this tab'
    },

    async toggleSoftphoneForCampaign(campaign) {
      if (this.isLiveSoftphoneCampaign(campaign)) {
        await this.goOfflineForCampaign()

        return
      }

      await this.goLiveForCampaign(campaign)
    },

    /**
     * Order matters and is not obvious.
     *
     * The server is told to go live FIRST - it pauses the agent's other softphone campaigns and
     * resumes this one - and only once that succeeds is the softphone told to connect, so a
     * go-live failure never leaves an agent taking calls for a campaign that is still paused.
     */
    async goLiveForCampaign(campaign) {
      // Belt and braces for the disabled button: never go live for a campaign the server is
      // guaranteed to refuse.
      if (this.isStrictPausedCampaign(campaign) || !canSoftphoneCampaignGoLive(campaign)) {
        showErrorMessage(this.softphoneButtonTitle(campaign))

        return
      }

      /**
       * Set BEFORE the first await, and it is the only thing holding the button.
       *
       * It is the first branch of `isSoftphoneButtonDisabled`, and nothing else disables the row
       * this early - `liveCampaignId` is still null until the server answers. Left until after the
       * gate, a second click during those round trips ran the whole thing again and sent a second
       * go-live for the same campaign. The gate below made that window twice as wide by putting
       * another request in front of the flag.
       */
      this.isTogglingSoftphone = true

      try {
        /**
         * Both caps, re-read at the point of no return, and both gating on the RESPONSE rather than
         * on anything already on screen.
         *
         * The balance because the store is last-writer-wins by arrival, so a slower overlapping
         * refresh can put an older reading back over the top. The campaign's budget because the row
         * NEVER carried it - the list endpoint cannot - so the only honest answer is a fetched one.
         *
         * In parallel: two independent reads of the same account, and going live is a click the
         * agent is waiting on.
         */
        const [freshAccountBalance, campaignBudgetState] = await Promise.all([
          this.refreshAccountBalanceBeforeGoingLive(),
          this.refreshCampaignBudgetBeforeGoingLive(campaign)
        ])

        const blockedReason = this.softphoneGoLiveBlockedReason(
          { ...campaign, ...campaignBudgetState },
          freshAccountBalance ?? this.accountBalance
        )

        if (blockedReason) {
          showErrorMessage(blockedReason)

          return
        }

        await this.goLiveSoftphoneCampaign({ campaignId: campaign.id, status: campaign.status })

        // The whole row: the action picks off what the softphone needs, including the budget's
        // minimum duration, which a tab that holds the softphone without holding this list has no
        // other way to learn.
        //
        // Not awaited, and its failure deliberately swallowed: going live reports itself through
        // the softphone's status, which is what `waitForSoftphoneToConnect` below reads - in this
        // tab or in whichever one owns the transport.
        Promise.resolve(this.requestGoOnline(campaign)).catch(() => {})

        // The server resuming the campaign is only half of going live - the agent is not taking
        // calls until the softphone has registered. Announcing success on the server call alone
        // told agents they were live while the softphone was still failing to connect.
        const connection = await this.waitForSoftphoneToConnect()

        // Called off by a Go offline the agent asked for, or by a phone hiding the page: what was
        // asked for has happened, and the panel gives the reason when there is one.
        if (connection.status === 'cancelled') {
          return
        }

        if (connection.status === 'failed') {
          showErrorMessage(connection.error || 'The phone could not connect, so you are not taking calls')

          return
        }

        if (connection.status === 'timedOut') {
          showWarningMessage('The campaign was resumed, but you are not connected yet - check the status in the header')

          return
        }

        // No success notification: the softphone panel opens itself on going live, and a panel
        // reading Ready says the same thing in the place the agent is about to work from. See the
        // `softphoneAttention` watcher in SoftphoneHeaderWidget.
      } catch (error) {
        showErrorMessage(error.response?.data?.title || 'Could not go live for this campaign')
      } finally {
        this.isTogglingSoftphone = false
      }
    },

    /**
     * Resolves once the softphone has said what became of the go-live: `connected` when it is
     * registered and taking calls, `failed` when it gave up (the token call, the transport, or the
     * owning tab going away on the way), `cancelled` when a Go offline that was asked for ended it
     * (see the store's `isOfflineRequested`), and `timedOut` when it has said nothing either way.
     *
     * Reads the softphone store's status and nothing else, which works whether this tab owns the
     * transport or is only being told about it.
     */
    waitForSoftphoneToConnect() {
      return new Promise(resolve => {
        let unwatch = null
        let timeoutId = null

        // A failed go-live resets the widget to OFFLINE and only then sets ERROR with the reason,
        // so a bare OFFLINE waits a moment for that reason rather than reporting without it.
        let failureGraceTimerId = null

        let hasStartedConnecting = false

        const finish = result => {
          if (unwatch) {
            unwatch()
            unwatch = null
          }

          clearTimeout(timeoutId)
          clearTimeout(failureGraceTimerId)

          timeoutId = null
          failureGraceTimerId = null

          resolve(result)
        }

        const fail = () => finish({ status: 'failed', error: this.$store.state.softphone.lastError })

        const isOfflineRequested = () => this.$store.state.softphone.isOfflineRequested === true

        // The grace's end: asked for after all (a mirror hears of it in the owner's next state), or
        // the failure it looked like.
        const settleOffline = () => (isOfflineRequested() ? finish({ status: 'cancelled' }) : fail())

        const evaluate = status => {
          if (status === SOFTPHONE_STATUS.CONNECTING) {
            hasStartedConnecting = true

            return false
          }

          if (CONNECTED_STATUSES.includes(status)) {
            finish({ status: 'connected' })

            return true
          }

          // ERROR is the softphone's own report that it could not go live - it broadcasts the
          // reason with it. OTHER_TAB is another tab taking the softphone over, which leaves this
          // go-live just as dead.
          if (status === SOFTPHONE_STATUS.ERROR || status === SOFTPHONE_STATUS.OTHER_TAB) {
            fail()

            return true
          }

          // Back to offline after it began connecting, because someone asked: the agent's Go offline,
          // or a phone hiding the page. The go-live was called off rather than failed, and reporting
          // it as "could not connect" would contradict the reason the panel shows.
          if (status === SOFTPHONE_STATUS.OFFLINE && hasStartedConnecting && isOfflineRequested()) {
            finish({ status: 'cancelled' })

            return true
          }

          // Back to offline after it began connecting: the softphone tore down, whether it went on
          // to say why or its tab simply went away. Offline BEFORE that is the softphone dropping a
          // previous campaign on its way to this one, which is part of a normal go-live.
          if (status === SOFTPHONE_STATUS.OFFLINE && hasStartedConnecting && !failureGraceTimerId) {
            failureGraceTimerId = setTimeout(settleOffline, SOFTPHONE_FAILURE_REASON_GRACE_MS)
          }

          return false
        }

        // The widget can register before this tab gets to subscribe.
        if (evaluate(this.$store.state.softphone.status)) {
          return
        }

        unwatch = this.$store.watch(state => state.softphone.status, evaluate)

        timeoutId = setTimeout(() => finish({ status: 'timedOut' }), SOFTPHONE_CONNECT_TIMEOUT_MS)
      })
    },

    /**
     * The owning tab goes offline end to end - it disconnects and pauses the campaign, in that
     * order, then broadcasts the result so every other tab's rows come back to life. When that tab
     * is this one the action simply does it; when it is another, this asks.
     */
    async goOfflineForCampaign() {
      try {
        this.isTogglingSoftphone = true

        // No success notification either way: the softphone panel and the row's own button both
        // say what happened, and they say it where the agent is looking.
        await this.requestGoOffline()
      } catch (error) {
        showErrorMessage('Could not take you offline')
      } finally {
        this.isTogglingSoftphone = false
      }
    },

    startGoLiveCooldown() {
      this.clearGoLiveCooldown()

      this.isGoLiveCoolingDown = true

      this.goLiveCooldownTimer = setTimeout(() => {
        this.goLiveCooldownTimer = null
        this.isGoLiveCoolingDown = false
      }, GO_LIVE_COOLDOWN_MS)
    },

    clearGoLiveCooldown() {
      if (this.goLiveCooldownTimer) {
        clearTimeout(this.goLiveCooldownTimer)
        this.goLiveCooldownTimer = null
      }

      this.isGoLiveCoolingDown = false
    },

    // Softphone campaigns open in their own reduced page - the standard edit page's Number/SIP
    // destination form cannot represent a softphone destination.
    editRouteName(campaign) {
      return isSoftphoneCampaign(campaign) ? 'BuyerEditSoftphoneCampaign' : 'BuyerEditCampaign'
    },

    // What a RingbaRTB or Programmatic campaign bids now, for its Bid cell (R65): inside the
    // rx-market-intelligence rollout only, like the own-bid line's terms, and null where there is no
    // number, which keeps "Dynamic". Never for an agent, who is not shown market data
    getBidColumnTermsFor(campaign) {
      if (!this.showMarketIntelligence || this.isStaticCampaign(campaign)) {
        return null
      }

      return getBidColumnTerms({
        marketPosition: campaign.marketPosition,
        routingType: this.getRoutingType(campaign),
        programmaticBidSettings: this.getProgrammaticBidSettings(campaign),
        isCpaOnly: this.isCpaOnlyCampaign(campaign)
      })
    },

    getMaxBidPerCall(campaign) {
      return campaign.routing.type === campaignRoutingTypes.Static ? this.formatCurrency(campaign.budget.maxBidPerCallInUSD) : 'Dynamic'
    },

    getCategoryMinBid(campaign) {
      return getStaticCampaignMinBid(campaign)
    },

    isStaticCampaign(campaign) {
      return campaign.routing.type === campaignRoutingTypes.Static
    },

    getRoutingType(campaign) {
      return campaign.routing?.type || ''
    },

    // Which of a Programmatic campaign's terms its API returns and which its settings fix (I16)
    getProgrammaticBidSettings(campaign) {
      return campaign.routing?.programmaticTarget?.bidSettings || null
    },

    isCpaOnlyCampaign(campaign) {
      return campaign.category?.isCpaConversionOnly === true
    },

    // The bid the endpoint positioned this campaign on, null when a dynamic campaign
    // placed no bids in the window (in which case there is no marker to draw)
    getPositionedBid(campaign) {
      return campaign.marketPosition ? campaign.marketPosition.positionedBidInUSD : null
    },

    getMissedCallsClass(campaign) {
      if (!campaign.marketPosition) {
        return 'text-muted'
      }

      switch (campaign.marketPosition.tier) {
      case bidTiers.PricedOut:
        return 'text-danger'
      case bidTiers.BelowMarket:
        return 'text-warning'
      default:
        return 'text-muted'
      }
    },

    // The banner's "Review below-market bids"
    sortByMissedCalls() {
      this.sortBy = 'missedCalls'
      this.sortDesc = true

      this.$track(USAGE_EVENTS.marketBannerReviewClicked.name)
    },

    showConfirmRaiseBid(bidRaise) {
      this.selectedBidRaise = bidRaise
      this.suggestionDismissTrigger = null
      this.suggestionConfirmTried = false

      this.$track(USAGE_EVENTS.marketSuggestionOpened.name, this.getSuggestionUsageContext(bidRaise, this.selectedBidRaiseCampaign))

      this.$bvModal.show('confirm-raise-bid-modal')
    },

    // The usage events' context for a chip's suggestion, against the campaign as the store has it
    getSuggestionUsageContext(bidRaise, campaign) {
      const { changesMinimum, isMinimumOnly, currentMinimumSeconds } = getSuggestionChange(bidRaise, campaign)

      return {
        campaignId: campaign.id,
        chip: bidRaise.fromSuggestedSetup ? 'setup' : 'legacy',
        // As the confirmation's title names it
        change: changesMinimum ? (isMinimumOnly ? 'minimum' : 'bid_and_minimum') : 'bid',
        routing: usageRoutingOf(this.getRoutingType(campaign)),
        currentBidInUSD: Number(campaign.budget.maxBidPerCallInUSD),
        suggestedBidInUSD: bidRaise.suggestedBidInUSD,
        currentMinimumSeconds,
        suggestedMinimumSeconds: changesMinimum ? bidRaise.minimumSeconds : null,
        missedCalls: bidRaise.missedCalls ?? null
      }
    },

    cancelSuggestion() {
      this.suggestionDismissTrigger = 'cancel'
      this.$bvModal.hide('confirm-raise-bid-modal')
    },

    // BootstrapVue's `hidden` event carries no trigger; `hide` does: 'headerclose', 'esc',
    // 'backdrop', or 'event' for a programmatic hide, Cancel's included. While a write is in flight
    // only the code closes the confirmation, so its outcome is all that is reported for it
    onSuggestionHide(bvEvent) {
      if (this.isLoading && bvEvent.trigger !== 'event') {
        bvEvent.preventDefault()

        return
      }

      if (!this.suggestionDismissTrigger) {
        this.suggestionDismissTrigger = bvEvent.trigger
      }
    },

    // applySuggestedBid clears the suggestion before it closes the confirmation, applied or refused,
    // and so do the watchers that close it when the market columns go. Still set here, and with no
    // Confirm clicked, the buyer closed it without one: Cancel, the header's ×, Esc or the backdrop
    onSuggestionHidden() {
      const trigger = this.suggestionDismissTrigger || null
      const confirmTried = this.suggestionConfirmTried

      this.suggestionDismissTrigger = null
      this.suggestionConfirmTried = false

      if (!this.selectedBidRaise || confirmTried) {
        return
      }

      this.$track(USAGE_EVENTS.marketSuggestionDismissed.name, {
        ...this.getSuggestionUsageContext(this.selectedBidRaise, this.selectedBidRaiseCampaign),
        trigger
      })
    },

    // A market tooltip opening anywhere in the list, once per kind per load
    onTooltipShown(bvEvent) {
      const target = bvEvent?.target

      if (!target || !this.$el.contains(target)) {
        return
      }

      const tooltip = MARKET_TOOLTIPS[target.getAttribute('data-cy')]

      if (tooltip) {
        this.$trackOnce(USAGE_EVENTS.marketTooltipShown.name, tooltip, { tooltip })
      }
    },

    // An inline save has put the new bid in the store, but the row's market position was scored
    // on the old one until it is refetched. Debounced because a run of arrow clicks saves once
    // per click, and every refetch is a ClickHouse query. The confirmation toast waits on the
    // same debounce, so a run of clicks confirms once, at the bid it ended on.
    onInlineBidSaved(campaign, bidInUSD, previousBidInUSD) {
      this.trackInlineBidSaved(campaign, bidInUSD, previousBidInUSD)

      // A long press released where it started (held at the minimum, or let go before its first
      // step) re-saves the bid it already had, which is no update to confirm. It still moves a
      // toast already pending: a click up and back down before the first save lands sends both
      // from the same bid, and the toast would otherwise confirm the step the buyer undid
      if (bidInUSD !== previousBidInUSD || this.pendingInlineBidToasts[campaign.id]) {
        this.pendingInlineBidToasts = {
          ...this.pendingInlineBidToasts,
          [campaign.id]: { name: campaign.name, bidInUSD }
        }
      }

      clearTimeout(this.marketPositionsRefreshTimeout)

      this.marketPositionsRefreshTimeout = setTimeout(() => {
        this.marketPositionsRefreshTimeout = null
        this.showInlineBidToasts()
        this.loadCampaignMarketPositions()
      }, 1000)
    },

    // One toast per campaign saved since the last one. Named, because two rows edited within the
    // same second would otherwise read as the same bid twice. The toast's title is rendered as
    // HTML, so the name the buyer typed is escaped
    showInlineBidToasts() {
      Object.values(this.pendingInlineBidToasts).forEach(({ name, bidInUSD }) => {
        showSuccessMessage(`Bid on ${escapeHtml(name)} updated to ${formatCurrency(bidInUSD)}`)
      })

      this.pendingInlineBidToasts = {}
    },

    // What the row offered when the edit began. The row holds the new bid by now, which reads as a
    // stale position, so the cell is read at the bid it held before. The editor autosaves after a
    // pause, so one edit can send several of these: the dashboard counts campaigns, not events
    trackInlineBidSaved(campaign, bidInUSD, previousBidInUSD) {
      if (!this.showMarketContext) {
        return
      }

      const atPrevious = { ...campaign, budget: { ...campaign.budget, maxBidPerCallInUSD: previousBidInUSD } }
      const kind = this.getRowOpportunityKind(atPrevious)
      const position = campaign.marketPosition

      let rowSuggestion = 'none'
      let suggestedBidInUSD = null

      if (kind === opportunityKinds.RaiseChip || kind === opportunityKinds.SwitchChip) {
        rowSuggestion = 'chip'
        suggestedBidInUSD = position.suggestedSetup.bidInUSD
      } else if (kind === opportunityKinds.LegacyChip) {
        rowSuggestion = 'chip'
        suggestedBidInUSD = position.suggestedBidInUSD
      } else if (kind === opportunityKinds.Advice || kind === opportunityKinds.ConversionAdvice) {
        rowSuggestion = 'advice'
      }

      this.$track(USAGE_EVENTS.marketInlineBidSaved.name, {
        campaignId: campaign.id,
        source: BID_SOURCES.manual,
        rowSuggestion,
        previousBidInUSD: previousBidInUSD ?? null,
        bidInUSD,
        suggestedBidInUSD
      })
    },

    // A raise goes through today's update-bid; a suggestion that changes the minimum (Slice H's
    // switch chip) through update-bid-and-minimum. Either way the write says it came from a
    // suggestion (R48), which only the chips do: the inline bid editor sends no source.
    async applySuggestedBid() {
      // Every way out of here reports the suggestion confirmed, with its outcome
      this.suggestionConfirmTried = true

      const campaign = this.selectedBidRaiseCampaign
      const suggestion = this.selectedBidRaise
      const switchesMinimum = suggestion.changesMinimum === true && suggestion.minimumSeconds != null
      const suggestedCents = toCents(suggestion.suggestedBidInUSD)
      const currentCents = toCents(campaign.budget.maxBidPerCallInUSD)

      // Read before anything below clears the suggestion or the write changes the campaign
      const usageContext = this.getSuggestionUsageContext(suggestion, campaign)
      const trackConfirmed = outcome => this.$track(USAGE_EVENTS.marketSuggestionConfirmed.name, {
        ...usageContext,
        outcome,
        source: BID_SOURCES.suggestion
      })

      const refuse = (message, outcome) => {
        trackConfirmed(outcome)
        showWarningMessage(message)
        this.selectedBidRaise = null
        this.$bvModal.hide('confirm-raise-bid-modal')
      }

      // The chip is hidden while a row's position is stale, but this is the last stop before a
      // real write. A raise must raise the bid: a "raise" to or below the bid the campaign already
      // has would lower it. A switch never lowers the bid either, but may keep it when the minimum
      // shortens, the minimum-only suggestion (I19); keeping it with any other minimum would change
      // nothing the suggestion was for
      if (switchesMinimum) {
        const currentMinimum = getConfiguredMinimumSeconds(campaign)
        const shortensMinimum = currentMinimum !== null && suggestion.minimumSeconds < currentMinimum

        if (suggestedCents < currentCents || (suggestedCents === currentCents && !shortensMinimum)) {
          refuse('This campaign\'s bid or minimum has changed since this suggestion was made', 'stale')

          return
        }
      } else if (suggestedCents <= currentCents) {
        refuse('This campaign\'s bid is already at or above the suggested bid', 'already_at_or_above')

        return
      }

      // A raise that still leaves the bid under the category minimum keeps the campaign off
      const minBid = getStaticCampaignMinBid(campaign)
      if (toCents(this.selectedBidRaise.suggestedBidInUSD) < toCents(minBid)) {
        trackConfirmed('below_minimum_bid')
        showErrorMessage(getMinBidMessage(minBid))
        this.selectedBidRaise = null
        this.$bvModal.hide('confirm-raise-bid-modal')

        return
      }

      // The write landed, so a failed refresh after it is not a failed suggestion
      let applied = false

      try {
        this.isLoading = true

        if (switchesMinimum) {
          await this.updateCampaignBidAndMinimum({
            campaignId: campaign.id,
            bidValue: suggestion.suggestedBidInUSD,
            minimumDurationInSeconds: suggestion.minimumSeconds,
            source: bidChangeSources.Suggestion
          })

          showSuccessMessage('Successfully updated campaign bid and minimum')
        } else {
          await this.updateCampaignBid({
            campaignId: campaign.id,
            bidValue: suggestion.suggestedBidInUSD,
            source: bidChangeSources.Suggestion
          })

          showSuccessMessage('Successfully updated campaign bid')
        }

        applied = true
        trackConfirmed('applied')

        this.selectedBidRaise = null

        this.$bvModal.hide('confirm-raise-bid-modal')

        // Refresh both the campaigns (new bid) and the market positions (new tier). The positions
        // go through the same gate as on load, in case the flag turned off meanwhile.
        await Promise.all([this.fetchBuyerCampaigns(), this.loadCampaignMarketPositions()])
      } catch (error) {
        if (!applied) {
          trackConfirmed('error')
        }

        showErrorMessage(error.response?.data?.title
          || (switchesMinimum ? 'Failed to update campaign bid and minimum' : 'Failed to update campaign bid'))
      } finally {
        this.isLoading = false
      }
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep .panel__header h6 {
  flex-grow: 1;
}

// The stats panel and the list stack as on Call Logs (_call-logs-page.scss): no gap under the stats
// panel, a small one above the list. .panel too, so these beat the Panel's own margins
.panel.campaign-rpc-stats {
  margin-bottom: 0;
}

.panel.campaigns-panel--below-stats {
  margin-top: 0.5rem;
}

.campaign-rpc-stats ::v-deep .panel__content {
  // stylelint-disable-next-line declaration-no-important -- over the p-3 utility Panel puts on its content
  padding: 0.75rem !important;
}

// The Call Logs compact cards a size smaller, shared out evenly but never wider than 14rem, so on a
// wide screen they stay compact instead of stretching across the panel
.campaign-rpc-stats__cards {
  gap: 0.75rem;

  ::v-deep .card-stats {
    flex: 1 1 0;
    min-width: 9rem;
    max-width: 14rem;
    background-color: var(--component-panel__header-bg);
  }

  ::v-deep .card-stats__body {
    padding: 0.375rem 0.625rem;
  }

  ::v-deep .card-stats__icon {
    font-size: 1.125rem;
  }

  // Icons differ in width; a fixed column starts every card's text at the same place
  ::v-deep .card-stats .row > .col-auto:first-child {
    display: flex;
    justify-content: center;
    box-sizing: content-box;
    width: 1.25rem;
  }

  ::v-deep .card-stats .col-auto {
    // stylelint-disable-next-line declaration-no-important -- over the mb-2 utility ReportingCardStats puts on its icon column
    margin-bottom: 0 !important;
  }

  ::v-deep .card-title {
    font-size: 0.65rem;
    // stylelint-disable-next-line declaration-no-important -- over the mb-0 utility ReportingCardStats puts on its title
    margin-bottom: 0.125rem !important;
  }

  ::v-deep .h5 {
    font-size: 0.9rem;
  }
}

/*
 * The On call badge is the pale one - the widget leaves its own the colour of the panel behind it,
 * for a state the agent is already living through rather than one they have to be told about. That
 * only reads as a badge against a border: `badge-light` on a white table cell is a floating word.
 * The widget draws the same border on the same badge, for the same reason.
 */
.campaign-status.badge-light {
  box-shadow: inset 0 0 0 1px #ced4da;
}

/*
 * The bid editor is 33px tall in rows of 21px text lines, and the cells are top-aligned. Hung from
 * the top of the line and pulled 6px into the cell padding either side, it centres on the text in
 * the rest of the row, and a row that has it is no taller than one that reads "Dynamic".
 */
.inline-bid-input {
  vertical-align: top;
  margin: -6px 0;
}

// Hover text a finger cannot reach, written out wherever a finger is the pointer (below).
.campaign-go-live-reason {
  display: none;
}

// In the table, Go Live and edit stand 16px after the button before them. A card spaces its actions
// by the touch table's gap instead (_touch-table.scss).
.campaign-go-live,
.campaign-edit {
  margin-left: 1rem;
}

/*
 * Header labels sit on the bottom of the header row, but b-table centres the sort icon, which then
 * lines up with the first line of a two-line label. Hung from the bottom instead: the cell padding
 * plus about half of what a 1.5 line has over the 1em icon, so it sits on the label's last line.
 */
::v-deep table.table.b-table > thead > tr > th[aria-sort]:not(.b-table-sort-icon-left) {
  background-position-y: bottom calc(0.75rem + 0.2em);
}

// A header whose label carries a hover explanation: a dotted underline says there is one
.campaign-head-hint {
  cursor: help;
  text-decoration: underline dotted;
  text-underline-offset: 3px;
}

// The "(today)" after the RPC, Calls and Total headers: small and dimmed, so the short labels lead
.campaign-rpc-head-today {
  font-size: 0.75em;
  font-weight: normal;
  opacity: 0.6;
}

/*
 * Go Live / Go Offline is a small button, 31px tall in rows of 21px text lines, and the cells are
 * top-aligned. As with the bid editor above: hung from the top of the line and pulled 5px into the
 * cell padding either side, it centres on the text in the rest of the row without making it taller.
 */
.campaign-go-live {
  vertical-align: top;
  margin-top: -5px;
  margin-bottom: -5px;
}

/*
 * From md up a buyer's table is wider than a phone on its side or a tablet has room for (830px
 * against 520 to 840px), and on a touch screen the touch table pins its actions to the table's right
 * edge. The pinned column is only as wide as its widest unbreakable piece, so its buttons wrapped one
 * under another, edit keeping its table margin. They stay on one line, 8px apart, Go Live's label
 * included: pinned, a wider column can no longer push Go Offline past the table's edge, as it did
 * before the column was pinned. A disabled Go Live's reason still wraps, 4px under the buttons.
 */
@include media-breakpoint-up(md) {
  @include coarse-pointer {
    ::v-deep td.touch-table__actions > div {
      white-space: nowrap;
    }

    .campaign-go-live,
    .campaign-edit {
      margin-left: 0;
    }

    ::v-deep td.touch-table__actions .btn + .btn {
      margin-left: 0.5rem;
    }

    .campaign-go-live-reason {
      margin-top: 0.25rem;
      white-space: normal;
    }
  }
}

// Wherever a finger is the pointer, a phone on its side included, a disabled Go Live's reason is
// written under it, because a finger can neither hover nor tap a disabled button. In a card it is a
// line of the actions' own, spaced from the buttons by the touch table's gap.
@include touch-screen {
  .campaign-go-live-reason {
    display: block;
    flex-basis: 100%;
  }

  // A card lays its actions out in a row of their own, with nothing to centre on
  .campaign-go-live {
    margin-top: 0;
    margin-bottom: 0;
  }
}
</style>
